import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Button,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useBreakpointValue,
    Badge,
    Icon,
    Divider,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { FaSync, FaChartLine, FaExclamationTriangle, FaUsers, FaShieldAlt, FaBrain, FaUserFriends, FaBoxOpen, FaClock, FaCheckCircle } from 'react-icons/fa';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    Legend,
    LineChart,
    Line,
} from 'recharts';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const BIAS_CODES = {
    OVERCONF: 'Overconfidence',
    LOSSAV: 'Loss Aversion',
    ANCHOR: 'Anchoring',
    HERD: 'Herd Behavior',
    FAMILIAR: 'Familiarity Bias',
    CONFIRM: 'Confirmation Bias',
    RECENCY: 'Recency Bias',
    AFFECT: 'Affect Infusion',
    MENTAL: 'Mental Accounting',
    IMPACT: 'Impact Bias',
    DISPOS: 'Disposition Effect',
    COGNITIVE: 'Cognitive Dissonance',
    TAXAV: 'Tax Aversion',
    REGRET: 'Regret Aversion',
    EMOTION: 'Emotional State',
};

function DashboardRMCustomerInsights() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [loading, setLoading] = useState(false);
    const [insightsData, setInsightsData] = useState(null);
    const toast = useToast();

    const isMobile = useBreakpointValue({ base: true, md: false });
    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const chartGridColor = isDarkTheme ? 'rgba(66, 153, 225, 0.25)' : '#E2E8F0';

    // Status colors - lighter for dark theme
    const successColor = isDarkTheme ? '#68D391' : '#38A169';
    const errorColor = isDarkTheme ? '#FC8181' : '#E53E3E';
    const warningColor = isDarkTheme ? '#F6AD55' : '#DD6B20';
    const yellowColor = isDarkTheme ? '#F6E05E' : '#D69E2E';

    // Info card backgrounds - improved transparency for dark theme
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '15';
    const successBg = isDarkTheme ? 'rgba(104, 211, 145, 0.15)' : '#D1FAE5';
    const errorBg = isDarkTheme ? 'rgba(252, 129, 129, 0.15)' : '#FEE2E2';
    const warningBg = isDarkTheme ? 'rgba(246, 173, 85, 0.15)' : '#FED7AA';
    const yellowBg = isDarkTheme ? 'rgba(246, 224, 94, 0.15)' : '#FEF3C7';
    const greenBg = isDarkTheme ? 'rgba(104, 211, 145, 0.15)' : '#E8F5E9';

    // Placeholder/empty background
    const placeholderBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.50';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Chart axis color
    const axisColor = isDarkTheme ? bodyTextColor : '#4A5568';

    // Chart colors - brighter for dark theme
    const chartPrimaryColor = isDarkTheme ? accentColor : primaryColor;

    // Professional color palette matching theme
    const AGE_COLORS = isDarkTheme 
        ? [accentColor, successColor, '#B794F4', warningColor, errorColor, yellowColor]
        : [primaryColor, successColor, '#805AD5', warningColor, errorColor, yellowColor];
    
    const SEGMENT_COLORS = isDarkTheme
        ? [accentColor, successColor, '#B794F4', warningColor, errorColor, yellowColor, '#4FD1C5', '#A0AEC0']
        : [primaryColor, successColor, '#805AD5', warningColor, errorColor, yellowColor, '#00B5D8', '#718096'];

    const fetchInsightsData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/content/rm-customer-insights`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setInsightsData(res.data);
        } catch (error) {
            console.error('Insights fetch error:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to load customer insights.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
            setInsightsData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInsightsData();
        // eslint-disable-next-line
    }, []);

    const renderTopBiases = () => {
        if (!insightsData?.top_biases_in_my_portfolio || insightsData.top_biases_in_my_portfolio.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No bias data available.</Text>
                </Flex>
            );
        }

        const biasData = insightsData.top_biases_in_my_portfolio.map((bias) => ({
            name: BIAS_CODES[bias.bias_code] || bias.bias_name,
            score: parseFloat(bias.mean_score.toFixed(2)),
            customers: bias.affected_customers,
        }));

        return (
            <ResponsiveContainer width="100%" height={350}>
                <LineChart data={biasData} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis
                        dataKey="name"
                        stroke={axisColor}
                        angle={-45}
                        textAnchor="end"
                        height={90}
                        tick={{ fontSize: isMobile ? 8 : 10, fill: axisColor }}
                    />
                    <YAxis domain={[0, 100]} stroke={axisColor} tick={{ fill: axisColor }} />
                    <RechartsTooltip
                        contentStyle={{ 
                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg, 
                            border: `1px solid ${borderColor}`, 
                            borderRadius: '8px',
                            color: isDarkTheme ? '#E2E8F0' : '#1A202C'
                        }}
                        formatter={(value, name, props) => [
                            `Score: ${value}, Affected: ${props.payload.customers} customers`,
                            'Bias Severity'
                        ]}
                    />
                    <Line
                        type="monotone"
                        dataKey="score"
                        stroke={chartPrimaryColor}
                        strokeWidth={3}
                        dot={{ fill: chartPrimaryColor, r: 5 }}
                        activeDot={{ r: 7 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        );
    };

    const renderAgeDistribution = () => {
        if (!insightsData?.age_distribution || insightsData.age_distribution.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No age distribution data available.</Text>
                </Flex>
            );
        }

        const ageData = insightsData.age_distribution.map((item, index) => ({
            name: item.bucket,
            value: item.count,
            percentage: item.percentage,
            color: AGE_COLORS[index % AGE_COLORS.length],
        }));

        return (
            <Box>
                <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                        <Pie
                            data={ageData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            dataKey="value"
                        >
                            {ageData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <RechartsTooltip
                            contentStyle={{ 
                                backgroundColor: isDarkTheme ? '#2D3748' : cardBg, 
                                border: `1px solid ${borderColor}`, 
                                borderRadius: '8px',
                                color: isDarkTheme ? '#E2E8F0' : '#1A202C'
                            }}
                            formatter={(value, name, props) => [
                                `${value} (${props.payload.percentage.toFixed(1)}%)`,
                                props.payload.name
                            ]}
                        />
                        <Legend
                            verticalAlign="bottom"
                            height={36}
                            wrapperStyle={{ fontSize: '12px', color: bodyTextColor }}
                        />
                    </PieChart>
                </ResponsiveContainer>

                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mt={4}>
                    {ageData.map((item, index) => (
                        <Card 
                            key={index} 
                            bg={placeholderBg} 
                            borderRadius="md" 
                            boxShadow="sm" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody p={3}>
                                <VStack spacing={1} align="start">
                                    <HStack>
                                        <Box w="10px" h="10px" bg={item.color} borderRadius="sm" />
                                        <Text fontSize="xs" fontWeight="bold" color={labelTextColor}>
                                            {item.name}
                                        </Text>
                                    </HStack>
                                    <Text fontSize="lg" fontWeight="bold" color={labelTextColor}>
                                        {item.value}
                                    </Text>
                                    <Text fontSize="xs" color={bodyTextColor}>
                                        {item.percentage.toFixed(1)}%
                                    </Text>
                                </VStack>
                            </CardBody>
                        </Card>
                    ))}
                </SimpleGrid>
            </Box>
        );
    };

    const renderSegmentsCards = () => {
        if (!insightsData?.segments_distribution || insightsData.segments_distribution.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No segment data available.</Text>
                </Flex>
            );
        }

        const segmentData = insightsData.segments_distribution
            .sort((a, b) => b.count - a.count)
            .map((item, index) => ({
                name: item.segment,
                count: item.count,
                percentage: item.percentage,
                color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
            }));

        return (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                {segmentData.map((segment, index) => (
                    <Card
                        key={index}
                        bg={cardBg}
                        borderRadius="lg"
                        boxShadow="md"
                        borderWidth="2px"
                        borderColor={segment.color}
                        transition="all 0.2s"
                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                    >
                        <CardBody>
                            <VStack align="start" spacing={2}>
                                <Badge colorScheme="blue" fontSize="xs" mb={1}>
                                    Segment
                                </Badge>
                                <Heading size="sm" color={labelTextColor} noOfLines={2} minH="40px">
                                    {segment.name}
                                </Heading>
                                <Divider borderColor={borderColor} />
                                <HStack justify="space-between" w="100%">
                                    <VStack align="start" spacing={0}>
                                        <Text fontSize="2xl" fontWeight="bold" color={segment.color}>
                                            {segment.count}
                                        </Text>
                                        <Text fontSize="xs" color={bodyTextColor}>
                                            customers
                                        </Text>
                                    </VStack>
                                    <Badge 
                                        colorScheme="gray" 
                                        fontSize="lg" 
                                        px={3} 
                                        py={1}
                                        bg={segment.color}
                                        color="white"
                                    >
                                        {segment.percentage.toFixed(1)}%
                                    </Badge>
                                </HStack>
                            </VStack>
                        </CardBody>
                    </Card>
                ))}
            </SimpleGrid>
        );
    };

    const renderSegmentsChart = () => {
        if (!insightsData?.segments_distribution || insightsData.segments_distribution.length === 0) {
            return null;
        }

        const segmentData = insightsData.segments_distribution
            .sort((a, b) => b.count - a.count)
            .map((item, index) => ({
                name: item.segment,
                count: item.count,
                percentage: item.percentage,
                color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
            }));

        return (
            <ResponsiveContainer width="100%" height={350}>
                <BarChart data={segmentData} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis
                        dataKey="name"
                        stroke={axisColor}
                        angle={-45}
                        textAnchor="end"
                        height={90}
                        tick={{ fontSize: isMobile ? 8 : 10, fill: axisColor }}
                    />
                    <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                    <RechartsTooltip
                        contentStyle={{ 
                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg, 
                            border: `1px solid ${borderColor}`, 
                            borderRadius: '8px',
                            color: isDarkTheme ? '#E2E8F0' : '#1A202C'
                        }}
                        formatter={(value, name, props) => [
                            `${value} customers (${props.payload.percentage.toFixed(1)}%)`,
                            'Count'
                        ]}
                    />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                        {segmentData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        );
    };

    const renderPortfolioProductRecommendations = () => {
        if (!insightsData?.portfolio_product_recommendations || insightsData.portfolio_product_recommendations.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No product recommendations available.</Text>
                </Flex>
            );
        }

        return (
            <VStack spacing={4} align="stretch">
                {insightsData.portfolio_product_recommendations.map((product, index) => (
                    <Card
                        key={index}
                        bg={cardBg}
                        borderRadius="lg"
                        boxShadow="md"
                        borderWidth="2px"
                        borderColor={isDarkTheme ? accentColor : primaryColor}
                        transition="all 0.2s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                    >
                        <CardBody>
                            <VStack spacing={4} align="stretch">
                                {/* Product Header */}
                                <Flex justify="space-between" align="start" flexWrap="wrap" gap={2}>
                                    <VStack align="start" spacing={1} flex={1}>
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            {product.product_type}
                                        </Heading>
                                        <Badge colorScheme="blue" fontSize="sm">
                                            {product.product_code}
                                        </Badge>
                                    </VStack>
                                    <Badge colorScheme="green" fontSize="lg" px={4} py={2}>
                                        {(product.avg_confidence * 100).toFixed(0)}% Confidence
                                    </Badge>
                                </Flex>

                                <Divider borderColor={borderColor} />

                                {/* Product Stats */}
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Card 
                                        bg={infoBg} 
                                        borderRadius="md" 
                                        borderWidth="1px" 
                                        borderColor={isDarkTheme ? 'rgba(66, 153, 225, 0.4)' : primaryColor}
                                    >
                                        <CardBody p={4}>
                                            <Stat>
                                                <StatLabel fontSize="sm" color={bodyTextColor}>
                                                    Recommended For
                                                </StatLabel>
                                                <StatNumber fontSize="2xl" color={isDarkTheme ? accentColor : primaryColor}>
                                                    {product.recommended_for_count} Customers
                                                </StatNumber>
                                            </Stat>
                                        </CardBody>
                                    </Card>

                                    <Card 
                                        bg={greenBg} 
                                        borderRadius="md" 
                                        borderWidth="1px" 
                                        borderColor={successColor}
                                    >
                                        <CardBody p={4}>
                                            <Stat>
                                                <StatLabel fontSize="sm" color={bodyTextColor}>
                                                    Average Confidence
                                                </StatLabel>
                                                <StatNumber fontSize="2xl" color={successColor}>
                                                    {(product.avg_confidence * 100).toFixed(1)}%
                                                </StatNumber>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                </SimpleGrid>

                                {/* Biases Addressed */}
                                {product.top_biases_addressed && product.top_biases_addressed.length > 0 && (
                                    <Box>
                                        <Text fontSize="sm" fontWeight="bold" color={labelTextColor} mb={2}>
                                            Top Biases Addressed:
                                        </Text>
                                        <Wrap spacing={2}>
                                            {product.top_biases_addressed.map((bias, biasIndex) => (
                                                <WrapItem key={biasIndex}>
                                                    <Badge
                                                        colorScheme="blue"
                                                        fontSize="sm"
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                    >
                                                        {bias.replace(/_/g, ' ')}
                                                    </Badge>
                                                </WrapItem>
                                            ))}
                                        </Wrap>
                                    </Box>
                                )}
                            </VStack>
                        </CardBody>
                    </Card>
                ))}
            </VStack>
        );
    };

    const summary = insightsData?.summary;
    const attention = insightsData?.attention_needed;

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }} bg={backgroundColor}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Header */}
                    <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
                        <Box flex="1">
                            <Heading
                                size={{ base: 'lg', md: 'xl' }}
                                color={headingTextColor}
                                mb={2}
                                fontWeight="bold"
                            >
                                <HStack spacing={3}>
                                    <Icon as={FaChartLine} boxSize={{ base: 6, md: 8 }} />
                                    <Text>My Portfolio Insights</Text>
                                </HStack>
                            </Heading>
                            <Text color={bodyTextColor} fontSize="sm">
                                Comprehensive view of your customer portfolio
                            </Text>
                        </Box>
                        <Button
                            leftIcon={<FaSync />}
                            bg={primaryColor}
                            color="white"
                            size={{ base: 'sm', md: 'md' }}
                            onClick={fetchInsightsData}
                            isLoading={loading}
                            _hover={{ bg: buttonHoverBg }}
                        >
                            Refresh
                        </Button>
                    </Flex>

                    {loading && (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor}>Loading insights...</Text>
                            </VStack>
                        </Flex>
                    )}

                    {!loading && insightsData && (
                        <>
                            {/* Summary Statistics */}
                            {summary && (
                                <>
                                    <Heading size="md" color={headingTextColor} mb={2}>
                                        Summary Statistics
                                    </Heading>
                                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4 }}>
                                        <Card 
                                            bg={cardBg} 
                                            borderRadius="xl" 
                                            boxShadow="md" 
                                            border="1px" 
                                            borderColor={borderColor}
                                        >
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                            Total Customers
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={isDarkTheme ? accentColor : primaryColor} fontWeight="bold">
                                                        {summary.total_customers}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card 
                                            bg={cardBg} 
                                            borderRadius="xl" 
                                            boxShadow="md" 
                                            border="1px" 
                                            borderColor={borderColor}
                                        >
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaExclamationTriangle} color={errorColor} boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                            High Risk
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={errorColor} fontWeight="bold">
                                                        {summary.high_risk_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card 
                                            bg={cardBg} 
                                            borderRadius="xl" 
                                            boxShadow="md" 
                                            border="1px" 
                                            borderColor={borderColor}
                                        >
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaShieldAlt} color={warningColor} boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                            Medium Risk
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={warningColor} fontWeight="bold">
                                                        {summary.medium_risk_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card 
                                            bg={cardBg} 
                                            borderRadius="xl" 
                                            boxShadow="md" 
                                            border="1px" 
                                            borderColor={borderColor}
                                        >
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaCheckCircle} color={successColor} boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                            Low Risk
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={successColor} fontWeight="bold">
                                                        {summary.low_risk_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>
                                    </SimpleGrid>

                                    {/* Urgency Metrics */}
                                    <Heading size="sm" color={headingTextColor} mt={4} mb={2}>
                                        Urgency Levels
                                    </Heading>
                                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4 }}>
                                        <Card 
                                            bg={errorBg} 
                                            borderRadius="xl" 
                                            boxShadow="sm" 
                                            border="1px" 
                                            borderColor={errorColor}
                                        >
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaClock} color={errorColor} boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={labelTextColor}>
                                                            Critical
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={errorColor} fontWeight="bold">
                                                        {summary.critical_urgency_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card 
                                            bg={warningBg} 
                                            borderRadius="xl" 
                                            boxShadow="sm" 
                                            border="1px" 
                                            borderColor={warningColor}
                                        >
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaClock} color={warningColor} boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={labelTextColor}>
                                                            High
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={warningColor} fontWeight="bold">
                                                        {summary.high_urgency_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card 
                                            bg={yellowBg} 
                                            borderRadius="xl" 
                                            boxShadow="sm" 
                                            border="1px" 
                                            borderColor={yellowColor}
                                        >
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaClock} color={yellowColor} boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={labelTextColor}>
                                                            Medium
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={yellowColor} fontWeight="bold">
                                                        {summary.medium_urgency_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card 
                                            bg={successBg} 
                                            borderRadius="xl" 
                                            boxShadow="sm" 
                                            border="1px" 
                                            borderColor={successColor}
                                        >
                                            <CardBody p={{ base: 4, md: 5 }}>
                                                <Stat size={statSize}>
                                                    <HStack spacing={2} mb={1}>
                                                        <Icon as={FaClock} color={successColor} boxSize={{ base: 4, md: 5 }} />
                                                        <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={labelTextColor}>
                                                            Low
                                                        </StatLabel>
                                                    </HStack>
                                                    <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={successColor} fontWeight="bold">
                                                        {summary.low_urgency_count}
                                                    </StatNumber>
                                                </Stat>
                                            </CardBody>
                                        </Card>
                                    </SimpleGrid>

                                    {/* Average Priority Score */}
                                    <Card 
                                        bg={infoBg} 
                                        borderRadius="xl" 
                                        boxShadow="md" 
                                        border="1px" 
                                        borderColor={isDarkTheme ? 'rgba(66, 153, 225, 0.4)' : primaryColor}
                                    >
                                        <CardBody p={{ base: 4, md: 5 }}>
                                            <Stat size={statSize}>
                                                <HStack spacing={2} mb={1}>
                                                    <Icon as={FaChartLine} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                                    <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                        Average Priority Score
                                                    </StatLabel>
                                                </HStack>
                                                <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={isDarkTheme ? accentColor : primaryColor} fontWeight="bold">
                                                    {summary.average_priority_score?.toFixed(2)}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>Portfolio average</StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                </>
                            )}

                            {/* Attention Needed */}
                            {attention && (
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
                                    <Card 
                                        bg={warningBg} 
                                        borderRadius="xl" 
                                        boxShadow="sm" 
                                        border="1px" 
                                        borderColor={warningColor}
                                    >
                                        <CardBody p={{ base: 4, md: 5 }}>
                                            <Stat size={statSize}>
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={labelTextColor}>
                                                    High Priority Customers
                                                </StatLabel>
                                                <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={warningColor} fontWeight="bold">
                                                    {attention.high_priority_count}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>Needs immediate attention</StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>

                                    <Card 
                                        bg={placeholderBg} 
                                        borderRadius="xl" 
                                        boxShadow="sm" 
                                        border="1px" 
                                        borderColor={borderColor}
                                    >
                                        <CardBody p={{ base: 4, md: 5 }}>
                                            <Stat size={statSize}>
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Customers Without Scores
                                                </StatLabel>
                                                <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={bodyTextColor} fontWeight="bold">
                                                    {attention.no_scores_count}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>Pending analysis</StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                </SimpleGrid>
                            )}

                            {/* Portfolio Product Recommendations */}
                            {insightsData.portfolio_product_recommendations && insightsData.portfolio_product_recommendations.length > 0 && (
                                <Card 
                                    bg={cardBg} 
                                    borderRadius="lg" 
                                    boxShadow="md" 
                                    borderWidth="1px" 
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaBoxOpen} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Portfolio Product Recommendations
                                            </Heading>
                                        </HStack>
                                        <Text fontSize="sm" color={bodyTextColor} mb={4}>
                                            Products recommended for your portfolio based on customer behavioral profiles
                                        </Text>
                                        {renderPortfolioProductRecommendations()}
                                    </CardBody>
                                </Card>
                            )}

                            {/* Top Biases */}
                            <Card 
                                bg={cardBg} 
                                borderRadius="lg" 
                                boxShadow="md" 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Top Biases in My Portfolio
                                        </Heading>
                                    </HStack>
                                    <Text fontSize="sm" color={bodyTextColor} mb={4}>
                                        Top 5 behavioral biases affecting your customers
                                    </Text>
                                    {renderTopBiases()}
                                </CardBody>
                            </Card>

                            {/* Age Distribution */}
                            <Card 
                                bg={cardBg} 
                                borderRadius="lg" 
                                boxShadow="md" 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Age Distribution
                                        </Heading>
                                    </HStack>
                                    {renderAgeDistribution()}
                                </CardBody>
                            </Card>

                            {/* Behavioral Segments - Cards */}
                            <Card 
                                bg={cardBg} 
                                borderRadius="lg" 
                                boxShadow="md" 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaUserFriends} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Behavioral Segments
                                        </Heading>
                                    </HStack>
                                    <Text fontSize="sm" color={bodyTextColor} mb={4}>
                                        Distribution of customers across behavioral segments
                                    </Text>
                                    {renderSegmentsCards()}
                                </CardBody>
                            </Card>

                            {/* Segments Distribution Chart */}
                            <Card 
                                bg={cardBg} 
                                borderRadius="lg" 
                                boxShadow="md" 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <Heading size={{ base: 'sm', md: 'md' }} mb={4} color={headingTextColor}>
                                        Segments Distribution Chart
                                    </Heading>
                                    {renderSegmentsChart()}
                                </CardBody>
                            </Card>
                        </>
                    )}

                    {!loading && !insightsData && (
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <VStack py={10} spacing={4}>
                                    <Text fontSize="lg" color={mutedTextColor} fontWeight="medium">
                                        No insights data available
                                    </Text>
                                    <Text fontSize="sm" color={mutedTextColor}>
                                        Click refresh to load your portfolio insights
                                    </Text>
                                </VStack>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMCustomerInsights;